import React, { ReactNode, useState } from "react";
import { Box, Paper, Stack } from "@mui/material";
import { DensityLarge, DensityMedium, DensitySmall } from "@mui/icons-material";

import ColumnList from "./ColumnList";
import ColumnToolbar from "./ColumnToolbar";
import { ColumnDialogParams, ColumnModel, ColumnModelItem, ColumnToolbarType } from "./ColumnModel";
import RecordTableToolbar from "components/widgets/tables/RecordTable/RecordTableToolbar";

interface ColumnManagerProps {
  categoryLabel?: string;
  columnModel: ColumnModel
  onColumnDialogMode?: (params: ColumnDialogParams) => void;
  onRemoveColumn: (columnItem: ColumnModelItem) => void;
  onRemoveMultipleColumns: (activeColumns: ColumnModelItem[]) => void;
  toolbar?: ReactNode;
  toolbarType: ColumnToolbarType;
  updateColumnModel: (newColumnModel: ColumnModel) => void;
}

export const densityOptions = [
  { icon: <DensitySmall />, value: 'compact' },
  { icon: <DensityMedium />, value: 'standard' },
  { icon: <DensityLarge />, value: 'comfortable' },
];

export type ColumnItemType = {
  field: string;
  fieldType: string;
  headerName: string;
};

export default function ColumnManager(props: ColumnManagerProps) {
  const [activeColumns, setActiveColumns] = useState<ColumnModelItem[]>([]);
  
  // Keeping this for the time being in case we want to reimplement sorting later
  // const { sorting } = props.columnModel;
  // const [sortModel, setSortModel] = useState<GridSortModel | []>(sorting?.sortModel || []);

  let hiddenColumns: ColumnModelItem[] = [];
  let visibleColumns: ColumnModelItem[] = [];

  props.columnModel?.columns?.forEach((column: ColumnModelItem) => {
    if (column.hidden) {
      hiddenColumns.push(column);
    } else {
      visibleColumns.push(column);
    }
  });

  function swapColumnIndex(array: ColumnModelItem[], fromIndex: number, toIndex: number) {
    let temp = array[fromIndex];
    array[fromIndex] = array[toIndex];
    array[toIndex] = temp;

    return array;
  }

  // Moves column down one, then updates the column state
  function handleMoveColumnDown(colIndex: number) {
    // Swap current column with next column
    const swappedColumns = swapColumnIndex(visibleColumns, colIndex, colIndex + 1);
    const newColumnModelOrder = [...swappedColumns, ...hiddenColumns];
    const newColumnModelState = { ...props.columnModel, columns: newColumnModelOrder };
    visibleColumns = swappedColumns;
    props.updateColumnModel(newColumnModelState);
  }

  // Moves column up one, then updates the column state
  function handleMoveColumnUp(colIndex: number) {
    // Swap current column with previous column
    const swappedColumns = swapColumnIndex(visibleColumns, colIndex, colIndex - 1);
    const newColumnModelOrder = [...swappedColumns, ...hiddenColumns];
    const newColumnModelState = { ...props.columnModel, columns: newColumnModelOrder };
    visibleColumns = swappedColumns;
    props.updateColumnModel(newColumnModelState);
  }

  // Keeping this for the time being in case we want to reimplement sorting later
  /**
   * Sort a column by ascending, descending, or none.
   * Each click of the button toggles between 1 of the 3 choices.
   * @param colIndex 
   */
  // function handleSortColumn(colIndex: number | null) {
  //   if (colIndex === null) {
  //     handleClearSort();
  //     return;
  //   }

  //   const foundColTitle = visibleColumns[colIndex].field;
  //   const sortedColumn = props.columnModel.sorting?.sortModel?.[0]?.field ?? null;
  //   const currentSortDirection = props.columnModel.sorting?.sortModel?.[0]?.sort ?? null;

  //   const nextSortDirection = (currentSortDirection : string | null): GridSortDirection => {
  //     if (currentSortDirection === null ) return "asc";
  //     if (currentSortDirection === "asc" ) return "desc";
  //     return null;
  //   };

  //   const newSortDirection: GridSortDirection = sortedColumn === foundColTitle ? nextSortDirection(currentSortDirection) : "asc";
  //   const newSortModel: GridSortItem[] = newSortDirection ? [{ field: foundColTitle, sort: newSortDirection }] : [];
  //   const newColumnModelState = { ...props.columnModel, sorting: { sortModel: newSortModel } };

  //   setSortModel(newSortModel);
  //   props.updateColumnModel(newColumnModelState);
  // }

  // Keeping this for the time being in case we want to reimplement sorting later
  // Cancel currently applied sorting
  // function handleClearSort() {
  //   setSortModel([]);
  //   props.updateColumnModel({
  //     ...props.columnModel,
  //     sorting: { sortModel: [] }
  //   });
  // }

  // Toggle active a single column
  function handleToggleActiveColumn(column: ColumnModelItem) {
    const isColAlreadyActive = activeColumns.some(col => col.field === column.field);

    // If column already in active list, remove it
    if (isColAlreadyActive) {
      const foundIndex = activeColumns.findIndex(col => col.field === column.field);
      const activeColumnsCopy = activeColumns.toSpliced(foundIndex, 1);
      setActiveColumns(activeColumnsCopy);
    } else {
      // If column NOT already in list, add it
      setActiveColumns([...activeColumns, column]);
    }
  }

  // Toggle active all columns
  function handleToggleAllColumns(toggleOption?: "none") {
    if (toggleOption && toggleOption === "none") {
      setActiveColumns([]);
      return;
    }

    if (visibleColumns.length === activeColumns.length) {
      setActiveColumns([]);
    } else {
      setActiveColumns([...visibleColumns]);
    }
  }

  function handleDragColumn(sortedColumns: ColumnModelItem[]) {
    props.updateColumnModel({ ...props.columnModel, columns: sortedColumns });
  }

  return (
    <Stack>
      <Box sx={{ mt: 2 }} />
      <ColumnToolbar
        activeColumns={activeColumns}
        areAllActive={visibleColumns.length === activeColumns.length}
        onRemoveMultipleColumns={props.onRemoveMultipleColumns}
        moveColumnDown={handleMoveColumnDown}
        moveColumnUp={handleMoveColumnUp}
        toggleAllColumns={handleToggleAllColumns}
        visibleColumns={visibleColumns}
      >
        <Paper elevation={0} sx={{ padding: 1 }}>
          {props.toolbar}
          {props.toolbarType === "Topic Table" && (
            <RecordTableToolbar
              hiddenColumns={hiddenColumns}
              onColumnDialogAction={(params: ColumnDialogParams) => {
                props.onColumnDialogMode && props.onColumnDialogMode(params);
              }}
            />
          )}
        </Paper>
      </ColumnToolbar>
      <ColumnList
        activeColumns={activeColumns}
        moveColumnDown={handleMoveColumnDown}
        moveColumnUp={handleMoveColumnUp}
        onDragColumn={handleDragColumn}
        onRemoveColumn={props.onRemoveColumn}
        toggleActiveColumn={handleToggleActiveColumn}
        visibleColumns={visibleColumns}
        onColumnDialogMode={(params: ColumnDialogParams) => {
          if (props.onColumnDialogMode) {
            props.onColumnDialogMode(params);
          }
        }}
      />
    </Stack>
  );
}
